import { type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card } from './Card';

interface StatCardProps {
  label: string;
  value: number | string | undefined;
  icon?: LucideIcon;
  /** Optional trailing hint under the value (e.g. "vs last week"). */
  hint?: string;
  /** Directional tone for the hint. */
  trend?: 'up' | 'down' | 'neutral';
  className?: string;
}

const fmt = (v: number | string | undefined) =>
  v === undefined || v === null ? '—' : typeof v === 'number' ? v.toLocaleString() : v;

export function StatCard({ label, value, icon: Icon, hint, trend = 'neutral', className }: StatCardProps) {
  return (
    <Card className={cn('p-4 sm:p-5', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-2 text-2xl font-semibold tracking-tight">{fmt(value)}</p>
          {hint && (
            <p
              className={cn(
                'mt-1 text-xs',
                trend === 'up' && 'text-emerald-600 dark:text-emerald-400',
                trend === 'down' && 'text-destructive',
                trend === 'neutral' && 'text-muted-foreground',
              )}
            >
              {hint}
            </p>
          )}
        </div>
        {Icon && (
          <div className="rounded-lg bg-primary/10 p-2 text-primary">
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Card>
  );
}
